import axios from 'axios';
import { demoBooks } from '../datas/demoBooks';
import { demoMusic } from '../datas/demoMusic';
import { Companion } from '../types';
import apiBase from '../utils/baseApi';
import managementApi from '../utils/managementApi';

// Search API interfaces
export interface SearchResultData {
  // Contributor data
  id: string;
  givenName?: string;
  thumbnailUrl?: string;
  genre?: string[];
  // Album data
  albumName?: string;
  albumArtUrl?: string;
  category?: string;
  description?: string | null;
  // Track data
  trackName?: string;
  audioType?: string;
}

export interface SearchResultItem {
  id: string;
  type: 'Album' | 'Track' | 'Contributor';
  data: SearchResultData;
  score: number;
  matchedField: string;
}

export interface SearchResult {
  id: string;
  name: string;
  type: 'Album' | 'Track' | 'Artist' | 'Mentor';
  albumArtUrl?: string;
  thumbnailUrl?: string;
  contributors?: any[];
  albumName?: string;
  category?: string;
  description?: string | null;
  jobTitle?: string;
  headline?: string;
  slug?: string;
  callToAction?: string;
}

export interface MentorData {
  gender: string;
  firstName: string;
  company: string;
  headline: string;
  industry: string;
  jobTitle: string;
  lastName: string;
  location: string;
  mentorId: string;
  previewUrl: string;
  videoLoopUrl: string | null;
  department: string;
  race: string;
  isFeatured: boolean;
  slug: string;
  userId: string | null;
  call_to_action: string;
  isFavourite: boolean;
}

export interface SearchResponse {
  items: SearchResultItem[];
  total: number;
  searchType: string;
  query: string;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasNext: boolean;
    hasPrevious: boolean;
    totalPages: number;
    currentPage: number;
  };
}

// Helper function to transform API response to our expected format
const transformSearchResult = (item: SearchResultItem): SearchResult => {
  const { data, type } = item;

  if (type === 'Contributor') {
    return {
      id: data.id,
      name: data.givenName || 'Unknown Artist',
      type: 'Artist',
      thumbnailUrl: data.thumbnailUrl,
      albumArtUrl: data.thumbnailUrl,
    };
  } else if (type === 'Album') {
    return {
      id: data.id,
      name: data.albumName || 'Unknown Album',
      type: 'Album',
      albumArtUrl: data.albumArtUrl,
      thumbnailUrl: data.albumArtUrl,
      category: data.category,
      description: data.description,
    };
  } else if (type === 'Track') {
    return {
      id: data.id,
      name: data.trackName || 'Unknown Track',
      type: 'Track',
      albumArtUrl: undefined,
      thumbnailUrl: data.thumbnailUrl,
      description: data.description,
    };
  }

  // Fallback
  return {
    id: data.id,
    name: 'Unknown',
    type: 'Track',
  };
};

export const getAlbumsByCategory = async (
  category: string = 'music',
  limit: number = 10,
  offset: number = 0,
  genre?: string,
  search?: string,
): Promise<any> => {
  try {
    // Build query parameters
    let queryParams = `category=${category}&limit=${limit}&offset=${offset}&sort=albumName`;

    // Add genre parameter if provided and not "all"
    if (genre && genre.toLowerCase() !== 'all') {
      queryParams += `&genre=${encodeURIComponent(genre)}`;
    }

    // Add search parameter if provided
    if (search && search.trim()) {
      queryParams += `&search=${encodeURIComponent(search.trim())}`;
    }

    const response = await apiBase.get(`/api/albums?${queryParams}`);

    const apiItems = response.data.items;

    // For music category, insert demo music as the first item (only when no search or genre filter)
    if (category === 'music' && offset === 0 && !genre && !search) {
      const combinedItems = [];

      // Add demo music first
      combinedItems.push(...demoMusic);

      // Add all API items after demo music
      combinedItems.push(...apiItems);

      return {
        ...response.data,
        items: combinedItems,
        pagination: response.data.pagination, // Keep original pagination without modifying total
      };
    }

    // For books category, insert demo books as the third item
    if (category === 'book' && offset === 0) {
      // Insert demo books as the third item (after the first two API results)
      const combinedItems = [];

      // Add first two API items if they exist
      if (apiItems.length > 0) {
        combinedItems.push(apiItems[0]);
      }
      if (apiItems.length > 1) {
        combinedItems.push(apiItems[1]);
      }

      // Add all demo books
      combinedItems.push(...demoBooks);

      // Add remaining API items (skip the first two since we already added them)
      if (apiItems.length > 2) {
        combinedItems.push(...apiItems.slice(2));
      }

      return {
        ...response.data,
        items: combinedItems,
        pagination: response.data.pagination, // Keep original pagination without modifying total
      };
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching albums:', error);
    // Return empty response structure instead of null
    return {
      items: [],
      pagination: {
        hasNext: false,
        total: 0,
        limit,
        offset,
        hasPrevious: false,
        totalPages: 0,
        currentPage: 1,
      },
    };
  }
};

export const getFeaturedItems = async (): Promise<any> => {
  try {
    const response = await apiBase.get<{ url: string }>('/api/featured');
    return response.data;
  } catch (error) {
    console.error('Error fetching featured items:', error);
    return null; // Fallback to original URL
  }
};

/**
 * Fetch leaderboard albums sorted by likes (descending)
 */
export const getLeaderboardAlbums = async (
  category: 'music' | 'serenity' | 'book',
  limit: number = 12,
  offset: number = 0,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    const response = await apiBase.get<{ items: any[]; pagination: any }>(
      `/api/albums?category=${category}&limit=${limit}&offset=${offset}&sort=-likes`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching leaderboard ${category} albums:`, error);
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

/**
 * Fetch leaderboard tracks sorted by likes (descending)
 * @param limit - Number of items to fetch
 * @param offset - Offset for pagination
 * @param category - Optional category filter (music, serenity, book)
 */
export const getLeaderboardTracks = async (
  limit: number = 12,
  offset: number = 0,
  category?: string,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    let url = `/api/tracks?limit=${limit}&offset=${offset}&sort=-likes`;
    if (category) {
      url += `&category=${category}`;
    }
    const response = await apiBase.get<{ items: any[]; pagination: any }>(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching leaderboard tracks:', error);
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

/**
 * Fetch featured albums for home page (single API call for all categories)
 * Returns albums grouped by category: music, serenity, books
 * No pagination - loads all items at once
 */
export const getFeaturedAlbums = async (): Promise<{
  music: { items: any[]; count: number };
  serenity: { items: any[]; count: number };
  books: { items: any[]; count: number };
  'indian-music'?: { items: any[]; count: number };
  indianMusic?: { items: any[]; count: number };
}> => {
  try {
    const response = await apiBase.get('/api/featured/albums');
    return response.data;
  } catch (error) {
    console.error('Error fetching featured albums:', error);
    // Return empty structure on error
    return {
      music: { items: [], count: 0 },
      serenity: { items: [], count: 0 },
      books: { items: [], count: 0 },
      'indian-music': { items: [], count: 0 },
    };
  }
};

export const getAlbumById = async (albumId: string): Promise<any> => {
  // Check if this is a demo music item first
  const demoMusicItem = demoMusic.find((music) => music.id === albumId);
  if (demoMusicItem) {
    return demoMusicItem;
  }

  // Check if this is a demo book
  const demoBook = demoBooks.find((book) => book.id === albumId);
  if (demoBook) {
    return demoBook;
  }

  try {
    const response = await apiBase.get<{ url: string }>(
      `/api/albums/${albumId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

/**
 * Fetch batch album data (likes, publishedAt) for multiple albums
 * @param albumIds - Array of album IDs to fetch data for
 * @returns Object with album data including likes and publishedAt timestamp
 */
export const getBatchAlbumData = async (
  albumIds: string[],
): Promise<{
  items: Array<{ id: string; likes: number; publishedAt: number }>;
  total: number;
}> => {
  try {
    const response = await apiBase.post<{
      items: Array<{ id: string; likes: number; publishedAt: number }>;
      total: number;
    }>('/api/albums/batch', {
      albumIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching batch album data:', error);
    // Return empty response on error
    return { items: [], total: 0 };
  }
};

export const getTrackById = async (trackId: string): Promise<any> => {
  try {
    const response = await apiBase.get<{ url: string }>(
      `/api/tracks/${trackId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching track by ID:', error);
    return null; // Fallback to original URL
  }
};

export const getCreationById = async (
  orgId: string,
  trackId: string,
): Promise<any> => {
  try {
    const response = await apiBase.get<any>(
      `/api/creations/${orgId}/${trackId}`,
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching creation by ID:', error);
    // Return error info to distinguish between 404 and other errors
    if (error?.response?.status === 404) {
      return { error: 'not_found', status: 404 };
    }
    return null;
  }
};

export const addFavouriteAlbum = async (albumId: string): Promise<any> => {
  try {
    const response = await apiBase.post<{
      url: string;
      data: { albumId: string };
    }>(`/api/favorites/albums`, { albumId });
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const addFavouriteContributor = async (
  contributorId: string,
): Promise<any> => {
  try {
    const response = await apiBase.post<{
      url: string;
      data: { contributorId: string };
    }>(`/api/favorites/contributors`, { contributorId });
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const addFavouriteTrack = async (trackId: string): Promise<any> => {
  try {
    const response = await apiBase.post<{
      url: string;
      data: { trackId: string };
    }>(`/api/favorites/tracks`, { trackId });
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const getFavouriteAlbums = async (): Promise<any> => {
  try {
    const response = await apiBase.get<{ url: string }>(
      `/api/favorites/albums`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const getFavouriteContributors = async (): Promise<any> => {
  try {
    const response = await apiBase.get<{ url: string }>(
      `/api/favorites/contributors`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const getFavouriteTracks = async (): Promise<any> => {
  try {
    const response = await apiBase.get<{ url: string }>(
      `/api/favorites/tracks`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const getContributorById = async (
  contributorId: string,
): Promise<any> => {
  try {
    const response = await apiBase.get<{ url: string }>(
      `/api/contributors/${contributorId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching contributor by ID:', error);
    return null; // Fallback to original URL
  }
};

// Contributor Feeds API
export interface FeedItem {
  id: string;
  contributorId: string;
  caption: string;
  mediaType: 'video' | 'image';
  mediaUrl: string;
  thumbnailUrl: string | null;
  likesCount: number;
  commentsCount: number;
  publishedAt: number;
}

export interface FeedPagination {
  total: number;
  limit: number;
  offset: number;
  hasNext: boolean;
  hasPrevious: boolean;
  totalPages: number;
  currentPage: number;
}

export interface ContributorFeedsResponse {
  items: FeedItem[];
  pagination: FeedPagination;
}

export const getContributorFeeds = async (
  contributorId: string,
  limit: number = 10,
  offset: number = 0,
): Promise<ContributorFeedsResponse | null> => {
  try {
    const response = await apiBase.get<ContributorFeedsResponse>(
      `/api/feed?limit=${limit}&offset=${offset}&contributorId=${contributorId}&sort=-publishedAt`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching contributor feeds:', error);
    return null;
  }
};

// Fetch genres for filtering with category support
export const getGenres = async (
  category: string = 'music',
): Promise<string[]> => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log(`API: Fetching genres for category: ${category}`);
    }
    const response = await apiBase.get<{ genres: string[] }>(
      `/api/genres?category=${category}`,
    );
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `API: Received genres for ${category}:`,
        response.data.genres,
      );
    }
    return response.data.genres || [];
  } catch (error) {
    console.error(`API: Error fetching genres for ${category}:`, error);
    return [];
  }
};

// Paginated favorites API calls
export const getFavouriteAlbumsPaginated = async (
  limit: number = 12,
  offset: number = 0,
): Promise<{ items: any[]; hasNext: boolean }> => {
  try {
    const response = await apiBase.get<{ items: any[]; hasNext: boolean }>(
      `/api/favorites/albums?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated favorite albums:', error);
    return { items: [], hasNext: false };
  }
};

// Category-based paginated favorites API calls
export const getFavouriteAlbumsByCategory = async (
  category: 'music' | 'serenity' | 'book',
  limit: number = 12,
  offset: number = 0,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    const response = await apiBase.get<{ items: any[]; pagination: any }>(
      `/api/favorites/albums?category=${category}&limit=${limit}&offset=${offset}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching paginated favorite ${category} albums:`,
      error,
    );
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

export const getFavouriteTracksPaginated = async (
  limit: number = 12,
  offset: number = 0,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    const response = await apiBase.get<{ items: any[]; pagination: any }>(
      `/api/favorites/tracks?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated favorite tracks:', error);
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

export const getFavouriteContributorsPaginated = async (
  limit: number = 12,
  offset: number = 0,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    const response = await apiBase.get<{ items: any[]; pagination: any }>(
      `/api/favorites/contributors?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated favorite contributors:', error);
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

export const removeFavouriteAlbum = async (albumId: string): Promise<any> => {
  try {
    const response = await apiBase.delete<{
      url: string;
      data: { albumId: string };
    }>(`/api/favorites/albums/${albumId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const removeFavouriteContributor = async (
  contributorId: string,
): Promise<any> => {
  try {
    const response = await apiBase.delete<{
      url: string;
      data: { contributorId: string };
    }>(`/api/favorites/contributors/${contributorId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

export const removeFavouriteTrack = async (trackId: string): Promise<any> => {
  try {
    const response = await apiBase.delete<{
      url: string;
      data: { trackId: string };
    }>(`/api/favorites/tracks/${trackId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching album by ID:', error);
    return null; // Fallback to original URL
  }
};

// Companion favorites API functions
export const addFavouriteCompanion = async (
  companionId: string,
  slug: string,
): Promise<any> => {
  try {
    const response = await apiBase.post<{
      url: string;
      data: { companionId: string; slug: string };
    }>(`/api/favorites/companions`, { companionId, slug });
    return response.data;
  } catch (error) {
    console.error('Error adding companion to favorites:', error);
    return null;
  }
};

/** True/false per id — server uses DB + cache; not limited by list pagination. */
export const checkFavouriteCompanions = async (
  companionIds: string[],
): Promise<Record<string, boolean>> => {
  if (!companionIds.length) return {};
  try {
    const response = await apiBase.post<{
      favorited: Record<string, boolean>;
    }>(`/api/favorites/companions/check`, {
      companionIds: companionIds.map((id) => String(id)),
    });
    return response.data?.favorited ?? {};
  } catch (error) {
    console.error('Error checking favorite companions:', error);
    return {};
  }
};

export const getFavouriteCompanionsPaginated = async (
  limit: number = 12,
  offset: number = 0,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    const response = await apiBase.get<{ items: any[]; pagination: any }>(
      `/api/favorites/companions?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated favorite companions:', error);
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

export const removeFavouriteCompanion = async (
  companionId: string,
): Promise<any> => {
  try {
    const response = await apiBase.delete<{
      url: string;
      data: { companionId: string };
    }>(`/api/favorites/companions/${companionId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing companion from favorites:', error);
    return null;
  }
};

// Playlist API functions
export const getPlaylists = async (
  category: 'music' | 'serenity' | 'book' | 'creation',
  limit: number = 50,
  offset: number = 0,
  name?: string,
  trackId?: string,
): Promise<{ items: any[]; pagination: any }> => {
  try {
    let url = `/api/playlists?category=${category}&limit=${limit}&offset=${offset}`;
    if (name) {
      url += `&name=${encodeURIComponent(name)}`;
    }
    if (trackId) {
      url += `&trackId=${encodeURIComponent(trackId)}`;
    }
    const response = await apiBase.get<{ items: any[]; pagination: any }>(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching playlists for category ${category}:`, error);
    return { items: [], pagination: { hasNext: false, total: 0 } };
  }
};

export const createPlaylist = async (
  name: string,
  description: string,
  category: 'music' | 'serenity' | 'book' | 'creation',
  privacy: 'private' | 'public' | 'link' = 'private',
): Promise<any> => {
  try {
    const response = await apiBase.post<any>(`/api/playlists`, {
      name,
      description,
      category,
      privacy,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating playlist:', error);
    // Extract error message from API response and throw it
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to create playlist';
    throw new Error(errorMessage);
  }
};

export const addTrackToPlaylist = async (
  playlistId: string,
  trackId: string,
  options?: {
    itemType?: 'track' | 'creation';
    itemId?: string;
    creationOrgId?: string;
  },
): Promise<any> => {
  try {
    const payload: any = {};

    // If itemType is 'creation', use itemId; otherwise use trackId
    if (options?.itemType === 'creation') {
      payload.itemType = 'creation';
      payload.itemId = options.itemId;
      if (options.creationOrgId) {
        payload.creationOrgId = options.creationOrgId;
      }
    } else {
      payload.trackId = trackId;
      if (options?.itemType) {
        payload.itemType = options.itemType;
      }
    }

    const response = await apiBase.post<any>(
      `/api/playlists/${playlistId}/tracks`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    console.error('Error adding track to playlist:', error);
    // Extract error message from API response and make it user-friendly
    let errorMessage = error?.response?.data?.message || error?.message;

    // Check if it's a "track already exists" error and simplify the message
    if (errorMessage && errorMessage.includes('already in the playlist')) {
      errorMessage = 'This item already exists in the playlist';
    }

    throw new Error(errorMessage || 'Failed to add item to playlist');
  }
};

export const getPlaylistTracks = async (
  playlistId: string,
  limit?: number,
  offset?: number,
): Promise<any> => {
  try {
    let url = `/api/playlists/${playlistId}`;
    if (limit !== undefined && offset !== undefined) {
      url += `?limit=${limit}&offset=${offset}`;
    }
    const response = await apiBase.get<any>(url);
    const data = response.data;

    // Drop tracks the backend has marked unavailable (e.g. a creation that was
    // deleted from My Creations). Without this, deleted creations resurface as
    // "Deleted Track" ghost rows in the playlist UI.
    if (data) {
      const isUnavailable = (t: any) => t?.isAvailable === false;
      if (Array.isArray(data.tracks)) {
        data.tracks = data.tracks.filter((t: any) => !isUnavailable(t));
      } else if (data.tracks && Array.isArray(data.tracks.items)) {
        data.tracks.items = data.tracks.items.filter(
          (t: any) => !isUnavailable(t),
        );
      }
    }

    return data;
  } catch (error) {
    console.error('Error fetching playlist tracks:', error);
    return null;
  }
};

export const checkTracksInPlaylists = async (
  category: 'music' | 'serenity' | 'book' | 'creation',
  trackId: string,
  limit: number = 50,
  offset: number = 0,
): Promise<{ [playlistId: string]: boolean }> => {
  try {
    // Fetch playlists with trackId to get isTrackExists info with pagination
    const response = await apiBase.get<any>(
      `/api/playlists?category=${category}&limit=${limit}&offset=${offset}&trackId=${encodeURIComponent(
        trackId,
      )}`,
    );

    // Create a map of playlistId -> isTrackExists
    const trackExistsMap: { [playlistId: string]: boolean } = {};
    if (response.data?.items) {
      response.data.items.forEach((playlist: any) => {
        trackExistsMap[playlist.id] = playlist.isTrackExists ?? false;
      });
    }
    return trackExistsMap;
  } catch (error) {
    console.error('Error checking tracks in playlists:', error);
    return {};
  }
};

export const removeTrackFromPlaylist = async (
  playlistId: string,
  trackId: string,
  itemType?: 'track' | 'creation',
): Promise<any> => {
  try {
    let url = `/api/playlists/${playlistId}/tracks/${trackId}`;
    if (itemType) {
      url += `?itemType=${itemType}`;
    }
    const response = await apiBase.delete<any>(url);
    return response.data;
  } catch (error) {
    console.error('Error removing track from playlist:', error);
    return null;
  }
};

export const deletePlaylist = async (playlistId: string): Promise<any> => {
  try {
    const response = await apiBase.delete<any>(`/api/playlists/${playlistId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting playlist:', error);
    return null;
  }
};

export const reorderPlaylistTracks = async (
  playlistId: string,
  trackId: string,
  prevTrackId: string | null,
  nextTrackId: string | null,
  itemType?: 'track' | 'creation',
): Promise<any> => {
  try {
    const payload: any = { trackId };
    if (prevTrackId) {
      payload.prevTrackId = prevTrackId;
    }
    if (nextTrackId) {
      payload.nextTrackId = nextTrackId;
    }
    if (itemType) {
      payload.itemType = itemType;
    }

    const response = await apiBase.put<any>(
      `/api/playlists/${playlistId}/tracks/reorder`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error('Error reordering playlist tracks:', error);
    return null;
  }
};

export const updatePlaylist = async (
  playlistId: string,
  name: string,
  description: string,
): Promise<any> => {
  try {
    const response = await apiBase.patch<any>(`/api/playlists/${playlistId}`, {
      name,
      description,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating playlist:', error);
    // Extract error message from API response and throw it
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to update playlist';
    throw new Error(errorMessage);
  }
};

// Search API functions
export const searchContent = async (
  query: string,
  type?: 'Album' | 'Track' | 'Artist',
  limit: number = 10,
  offset: number = 0,
): Promise<{
  items: SearchResult[];
  pagination: { hasNext: boolean; total: number };
}> => {
  try {
    let url = `/api/search?q=${encodeURIComponent(
      query,
    )}&&minScore=1.0&limit=${limit}&offset=${offset}`;
    if (type) {
      // Map our types to API types
      const apiType = type === 'Artist' ? 'Contributor' : type;
      url += `&type=${apiType}`;
    }

    const response = await apiBase.get<SearchResponse>(url);
    const transformedItems = response.data.items.map(transformSearchResult);

    return {
      items: transformedItems,
      pagination: {
        hasNext: response.data.pagination.hasNext,
        total: response.data.pagination.total,
      },
    };
  } catch (error) {
    console.error('Error searching content:', error);
    return {
      items: [],
      pagination: {
        hasNext: false,
        total: 0,
      },
    };
  }
};

// Quick search for dropdown (limited results)
export const quickSearch = async (query: string): Promise<SearchResult[]> => {
  try {
    if (!query.trim()) return [];

    const [contentResults, mentorResults] = await Promise.all([
      searchContent(query, undefined, 3), // Limit to 3 results for dropdown
      searchMentors(query, 3), // Limit to 3 mentor results
    ]);

    // Combine results: content first, then mentors
    return [...contentResults.items, ...mentorResults];
  } catch (error) {
    console.error('Error in quick search:', error);
    return [];
  }
};

// Companions API
const getCompanionsJsonUrl = () => {
  switch (import.meta.env.MODE) {
    case 'staging':
      return {
        all: 'https://syntheticstoragestage-cdn-bcgra9aghgbrbnew.z02.azurefd.net/cache/mentors.json',
        featured:
          'https://syntheticstoragestage-cdn-bcgra9aghgbrbnew.z02.azurefd.net/cache/mentors_featured.json',
      };
    case 'station':
    case 'production':
      return {
        all: 'https://syntheticdatastorageprod-cdn-hpgkdta8a7eehggt.z02.azurefd.net/cache/mentors.json',
        featured:
          'https://syntheticdatastorageprod-cdn-hpgkdta8a7eehggt.z02.azurefd.net/cache/mentors_featured.json',
      };

    default:
      return {
        all: 'https://mentordata-acazgce6cvdzgng9.z02.azurefd.net/cache/mentors.json',
        featured:
          'https://mentordata-acazgce6cvdzgng9.z02.azurefd.net/cache/mentors_featured.json',
      };
  }
};

export const getFeaturedCompanions = async (): Promise<Companion[]> => {
  try {
    const { featured } = getCompanionsJsonUrl();
    const response = await axios.get<Companion[]>(featured);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching featured companions:', error);
    return [];
  }
};

export const getAllCompanions = async (): Promise<Companion[]> => {
  try {
    const { all } = getCompanionsJsonUrl();
    const response = await axios.get<{
      data: { searchMentors: { collection: Companion[] } };
    }>(all);
    return response.data?.data?.searchMentors?.collection || [];
  } catch (error) {
    console.error('Error fetching all companions:', error);
    return [];
  }
};

// Search mentors from companion API
export const searchMentors = async (
  query: string,
  limit: number = 6,
): Promise<SearchResult[]> => {
  try {
    if (!query.trim()) return [];

    const response = await managementApi.get<MentorData[]>(
      `/v2/mentors?search=${encodeURIComponent(query)}&size=${limit}`,
    );

    if (!Array.isArray(response.data)) {
      return [];
    }

    // Transform mentor data to SearchResult format
    const transformedMentors: SearchResult[] = response.data.map((mentor) => ({
      id: mentor.mentorId,
      name: `${mentor.firstName} ${mentor.lastName}`,
      type: 'Mentor',
      thumbnailUrl: mentor.previewUrl,
      albumArtUrl: mentor.previewUrl,
      jobTitle: mentor.jobTitle,
      headline: mentor.headline,
      slug: mentor.slug,
      callToAction: mentor.call_to_action,
    }));

    return transformedMentors;
  } catch (error) {
    console.error('Error searching mentors:', error);
    return [];
  }
};
